// src/components/PMAY/PMAY2Page.jsx
import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import axios from 'axios';
import { motion } from 'framer-motion';
import pmayImg from '../../img/pmay.jpg'; // <-- correct path to src/img/pmay.jpg
import { Link } from 'react-router-dom';
import { ChevronRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PMAY2Page = () => {
  const [banner, setBanner] = useState(null);
  const [overview, setOverview] = useState(null);
  const [eligibility, setEligibility] = useState({
    settings: null,
    tableData: null,
    loanLimits: null,
    subsidyDetails: null,
    additionalConditions: null
  });
  const [loading, setLoading] = useState(true);

  // Helper to safely parse JSON
  const safeJsonParse = (str, fallback = []) => {
    if (!str) return fallback;
    if (typeof str !== 'string') return str;
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error('JSON Parse error:', e, 'for string:', str);
      return fallback;
    }
  };

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get('/api/admin/banners/page/pmay');
        console.log('PMAY Banner Data:', response.data);
        if (response.data.success && response.data.data) {
          const activeBanners = response.data.data;
          if (Array.isArray(activeBanners) && activeBanners.length > 0) {
            setBanner(activeBanners[0]);
          } else if (!Array.isArray(activeBanners)) {
            setBanner(activeBanners);
          }
        }
      } catch (error) {
        console.error('Error fetching PMAY banner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  // Fetch Overview data
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await axios.get('/api/admin/loan-overview/pmay');
        console.log('PMAY Overview Data:', response.data);
        if (response.data.success) {
          setOverview(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching PMAY overview:', error);
      }
    };
    fetchOverview();
  }, []);

  // Fetch Eligibility data
  useEffect(() => {
    const fetchEligibility = async () => {
      try {
        const settingsRes = await axios.get('/api/admin/eligibility/settings/pmay');
        console.log('PMAY Eligibility Data:', settingsRes.data);
        if (settingsRes.data.data) {
          const settings = settingsRes.data.data;
          setEligibility({
            settings: settings,
            tableData: settings.table_data ? JSON.parse(settings.table_data) : null,
            loanLimits: settings.loan_limits ? JSON.parse(settings.loan_limits) : null,
            subsidyDetails: settings.subsidy_details ? JSON.parse(settings.subsidy_details) : null,
            additionalConditions: settings.additional_conditions ? JSON.parse(settings.additional_conditions) : null
          });
        }
      } catch (error) {
        console.error('Error fetching PMAY eligibility:', error);
      }
    };
    fetchEligibility();
  }, []);

  // Parse highlights
  const highlights = banner?.highlights ? safeJsonParse(banner.highlights) : [];
  
  
  // Schema for SEO
  const pmaySchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans and housing finance support across various customer segments in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
         "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/pmay-2-0-scheme-apply-online/#webpage",
        "url": "https://ahamhfc.com/pmay-2-0-scheme-apply-online",
        "name": "PMAY 2.0 Scheme: Apply Online | Aham Housing Finance",
        "description": "Apply online for PMAY 2.0 (Urban), check eligibility, track application status, and explore subsidy and tax-saving benefits with Aham Housing Finance.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/pmay-2-0-scheme-apply-online/#service"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/pmay-2-0-scheme-apply-online/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/who-we-are-1768990249800-537074390.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "Service",
        "@id": "https://ahamhfc.com/pmay-2-0-scheme-apply-online/#service",
        "name": "PMAY 2.0 Scheme Support",
        "alternateName": "PMAY 2.0 (Urban) Application Support",
        "url": "https://ahamhfc.com/pmay-2-0-scheme-apply-online",
        "image": {
          "@id": "https://ahamhfc.com/pmay-2-0-scheme-apply-online/#primaryimage"
        },
        "description": "Support service to help eligible customers apply for PMAY 2.0 (Urban), check eligibility, track application status, and understand subsidy and tax benefits.",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Home buyers seeking PMAY 2.0 subsidy support"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "termsOfService": "PMAY 2.0 eligibility, subsidy, and tax benefits are subject to applicable scheme rules and customer eligibility."
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/pmay-2-0-scheme-apply-online/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "PMAY 2.0",
            "item": "https://ahamhfc.com/pmay-2-0-scheme-apply-online"
          }
        ]
      }
    ]
  };

  return (
    <section
      className="min-h-screen bg-gray-50 text-gray-900"
    >

      <SEO
        title="PMAY 2.0 Scheme: Apply Online & Get ₹1.80 Lakh Subsidy"
        description="Apply online for PMAY 2.0 with Aham Housing Finance. Check eligibility, claim subsidy benefits, and track your application today."
        keywords="Aham PMAY 2.0, Aham Housing Finance PMAY 2.0, PMAY 2.0, Pradhan Mantri Awas Yojana 2.0, PMAY Urban 2.0, PMAY home loan subsidy, affordable housing scheme India, housing for all scheme, EWS home loan subsidy, LIG home loan subsidy, MIG home loan subsidy, PMAY eligibility, PMAY interest subsidy, home loan subsidy up to 1.5 lakh, PMAY loan up to 25 lakh, PMAY house value 35 lakh, female ownership PMAY, urban affordable housing finance, Aham PMAY subsidy, Aham Housing Finance affordable housing"
        canonical="https://ahamhfc.com/pmay-2-0-scheme-apply-online"
        ogTitle="PMAY 2.0 Scheme: Apply Online & Get ₹1.80 Lakh Subsidy | Aham"
        ogDescription="Apply for PMAY 2.0 (Urban) online with Aham. Check eligibility for ₹1.80 lakh interest subsidy, track application status, & save with 80EEA tax benefits. Apply now!"
         schema={pmaySchema}
           />

      {/* Hero Banner Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${banner?.image_url || "img/pmay/Pmay-Banner-new.png"}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || "Your Home, Your Future — Claim PMAY Benefits Up to ₹1.80 Lakhs"}
            </h1>
            <p className="text-lg md:text-2xl text-white/90 font-medium mb-6 md:mb-8 leading-relaxed">
              {banner?.subtitle || (
                <>
                  Empowering urban families with affordable housing under the <strong>“Housing for All”</strong> mission.
                </>
              )}
            </p>

            {/* Highlights Display */}
            {highlights.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-8">
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.5 }}
                    className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full shadow-lg"
                  >
                    <CheckCircleIcon className="h-5 w-5 text-yellow-400" />
                    <span className="text-white font-medium text-sm md:text-base">
                      {highlight}
                    </span>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Breadcrumb Navigation */}
            <div className="flex items-center space-x-2">
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">PMAY 2.0</span>
            </div>

          </motion.div>
        </div>
      </section>



      {/* Urban 2.0 Overview Section */}
      <section className="relative py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">

        {/* Left Content */}
        <div className="w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left">
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            {overview?.title ? overview.title.split(' ')[0] : "About"}{' '}
            <span className="text-primary-600">
              {overview?.title ? overview.title.split(' ').slice(1).join(' ') : "Urban 2.0"}
            </span>
          </h2>

          <div className="text-gray-600 leading-relaxed mb-6 text-lg whitespace-pre-line">
            {overview?.description || (
              <>
                <p className="mb-4 text-center lg:text-left">
                  <strong className="text-primary-600">Urban 2.0</strong> is a flagship government initiative for affordable housing
                  for urban families in EWS, LIG, and MIG segments.
                </p>
                <p className="text-center lg:text-left">
                  It ensures secure, sustainable, and affordable homes with financial support to uplift families and empower communities.
                </p>
              </>
            )}
          </div>

          <button
            onClick={() => (window.location.href = '/contact-aham-housing-finance')}
            className="bg-[#ed2636] text-white px-6 py-2 rounded-lg shadow hover:bg-primary-700 transition w-fit"
          >
            Contact Us
          </button>
        </div>

        {/* Right Image Card */}
        <div className="w-full lg:w-1/2">
          <div className="bg-white shadow-xl rounded-xl overflow-hidden">
            <img
              src={overview?.image_url || pmayImg}
              alt="Aham Housing Finance expert assisting a woman head of family with PMAY 2.0 online application for home loan subsidy"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {overview?.title || "Supporting Urban Housing Needs"}
              </h3>
              <div className="space-y-3">
                {overview?.features ? (
                  safeJsonParse(overview.features).map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {feature}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Creating affordable housing opportunities for all urban families, ensuring a safe & secure future.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Content Sections */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pb-16">
        <div className="bg-white shadow-2xl rounded-3xl p-6 md:p-10 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-6 font-primary">
            {eligibility.settings?.section_title || "Eligibility Criteria"}
          </h2>
          {eligibility.settings?.section_subtitle && (
            <p className="text-gray-600 text-lg mb-6">{eligibility.settings.section_subtitle}</p>
          )}

          <div className="overflow-x-auto rounded-xl">
            <table className="min-w-full border border-gray-200 text-base">
              <thead className="bg-primary-600 text-white">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold rounded-tl-xl">Class Scheme Type</th>
                  <th className="py-4 px-6 text-left font-semibold">EWS</th>
                  <th className="py-4 px-6 text-left font-semibold">LIG</th>
                  <th className="py-4 px-6 text-left font-semibold rounded-tr-xl">MIG</th>
                </tr>
              </thead>
              <tbody className="bg-gray-50">
                <tr className="border-t border-gray-200">
                  <td className="py-4 px-6 font-medium">Annual Household Income</td>
                  <td className="py-4 px-6">{eligibility.tableData?.ews_income || "Up to ₹3 lakh p.a."}</td>
                  <td className="py-4 px-6">{eligibility.tableData?.lig_income || "Up to ₹6 lakh p.a."}</td>
                  <td className="py-4 px-6">{eligibility.tableData?.mig_income || "Up to ₹9 lakh p.a."}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 space-y-6">
            <h3 className="text-xl font-semibold text-primary-900">House Ownership</h3>
            <p className="text-gray-600 text-lg">
              {eligibility.tableData?.house_ownership_text || (
                <>
                  Applicants must not own a pucca house in India. Property must be registered in the name of
                  a <strong className="text-primary-600">female head</strong> or jointly.
                </>
              )}
            </p>
          </div>
        </div>

        {/* Loan & Property Limits Section */}
        {eligibility.loanLimits && (
          <div className="bg-white shadow-2xl rounded-3xl p-6 md:p-10 mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-6 font-primary">Loan & Property Limits</h2>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <li className="bg-primary-50 p-4 rounded-lg"><strong className="text-primary-600">Max Loan:</strong> {eligibility.loanLimits.max_loan}</li>
              <li className="bg-primary-50 p-4 rounded-lg"><strong className="text-primary-600">Max House Value:</strong> {eligibility.loanLimits.max_house_value}</li>
              <li className="bg-primary-50 p-4 rounded-lg"><strong className="text-primary-600">Max Carpet Area:</strong> {eligibility.loanLimits.max_carpet_area}</li>
            </ul>
          </div>
        )}

        {/* Subsidy Details Section */}
        {eligibility.subsidyDetails && (
          <div className="bg-white shadow-2xl rounded-3xl p-6 md:p-10 mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-6 font-primary">Subsidy Details</h2>
            <ul className="space-y-3">
              {eligibility.subsidyDetails.map((detail, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary-600 mt-1">✔</span>
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Additional Conditions Section */}
        {eligibility.additionalConditions && (
          <div className="bg-white shadow-2xl rounded-3xl p-6 md:p-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-6 font-primary">Additional Conditions</h2>
            <ul className="space-y-3">
              {eligibility.additionalConditions.map((condition, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary-600 mt-1">✔</span>
                  <span>{condition}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default PMAY2Page;
